import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { AuthService } from '../core/services/auth.service';
import { AssistantBadgeService } from '../core/services/assistant-badge.service';
import { UserModel } from '../core/models/app.models';

interface NavItem {
  label: string;
  icon: string;
  routerLink?: string;
  comingSoon?: boolean;
  badge?: number;
}

@Component({
  selector: 'app-secured',
  templateUrl: './secured.component.html',
  styleUrl: './secured.component.scss'
})
export class SecuredComponent implements OnInit {
  mainItems: NavItem[] = [];
  agendaItems: NavItem[] = [];
  secondaryItems: NavItem[] = [];

  currentUser: UserModel | null = null;
  sidebarVisible: boolean = false;

  // Dialog "Změnit heslo"
  changePasswordDialog: boolean = false;
  currentPassword: string = '';
  newPassword: string = '';
  confirmPassword: string = '';

  constructor(
    private authService: AuthService,
    private assistantBadgeService: AssistantBadgeService,
    private messageService: MessageService,
    private router: Router,
    private destroyRef: DestroyRef
  ) {
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });

    // Mobilní sidebar zavíráme až po dokončení navigace. Synchronní zavření
    // v (click) na položce se pralo s router navigací a PrimeNG maska (overlay)
    // pak zůstávala viset přes obsah.
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.sidebarVisible = false);
  }

  ngOnInit(): void {
    this.mainItems = [
      { label: 'Dashboard', icon: 'pi pi-home', routerLink: '/secured/dashboard' },
      { label: 'Přestupy', icon: 'pi pi-arrow-right-arrow-left', routerLink: '/secured/transfers' },
      { label: 'Mezi kluby', icon: 'pi pi-arrows-h', routerLink: '/secured/transfers-between' },
      { label: 'Hráči', icon: 'pi pi-users', routerLink: '/secured/players' },
      { label: 'Kluby', icon: 'pi pi-shield', routerLink: '/secured/clubs' },
      { label: 'Asistentka', icon: 'pi pi-sparkles', routerLink: '/secured/assistant' },
      { label: 'Zpětný import', icon: 'pi pi-upload', routerLink: '/secured/import' },
    ];
    this.agendaItems = [
      { label: 'Komunikace', icon: 'pi pi-inbox', routerLink: '/secured/communication' },
      { label: 'Dokumenty', icon: 'pi pi-file', comingSoon: true },
    ];
    this.secondaryItems = [
      { label: 'Uživatelé', icon: 'pi pi-user-edit', routerLink: '/secured/users' },
      { label: 'Znalosti', icon: 'pi pi-book', routerLink: '/secured/knowledge' },
      { label: 'Auditní log', icon: 'pi pi-history', routerLink: '/secured/audit' },
      { label: 'Přestupní řád', icon: 'pi pi-table', routerLink: '/secured/price-tables' },
      { label: 'Vzory smluv', icon: 'pi pi-file-word', routerLink: '/secured/contract-templates' },
    ];

    // Odznak "Asistentka" odebírá sdílený pendingCount$ (viz AssistantBadgeService) = otevřené
    // úkoly + čekající návrhy. TodoListComponent/ProposalsComponent po každé akci (odškrtnout/
    // nový úkol/schválit/upravit/zamítnout) zavolají assistantBadgeService.refresh() a odznak se
    // tím aktualizuje bez nutnosti restartu sidebaru.
    // pendingCount$ je root-singleton (přežívá SecuredComponent), takže odběr se musí ukončit
    // při zániku komponenty — jinak každý logout/login cyklus (SecuredComponent se vytváří znovu)
    // přidá další trvalý subscriber na app-lifetime BehaviorSubject.
    this.assistantBadgeService.pendingCount$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(count => {
      const assistantItem = this.mainItems.find(item => item.routerLink === '/secured/assistant');
      if (assistantItem) {
        assistantItem.badge = count;
      }
    });
    this.assistantBadgeService.refresh();
  }

  logout(): void {
    this.authService.logout();
  }

  openChangePasswordDialog(): void {
    this.currentPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.changePasswordDialog = true;
    this.sidebarVisible = false;
  }

  submitChangePassword(): void {
    if (!this.currentPassword || !this.newPassword || !this.confirmPassword) {
      this.messageService.add({ severity: 'warn', summary: 'Upozornění', detail: 'Vyplňte všechna pole.' });
      return;
    }

    if (this.newPassword.length < 8) {
      this.messageService.add({ severity: 'warn', summary: 'Upozornění', detail: 'Nové heslo musí mít alespoň 8 znaků.' });
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.messageService.add({ severity: 'warn', summary: 'Upozornění', detail: 'Nová hesla se neshodují.' });
      return;
    }

    this.authService.changePassword(this.currentPassword, this.newPassword).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Úspěch', detail: 'Heslo bylo změněno.' });
        this.changePasswordDialog = false;
      },
      error: (err) => this.messageService.add({ severity: 'error', summary: 'Chyba', detail: err?.error?.message || 'Nepodařilo se změnit heslo.' })
    });
  }

  get userInitials(): string {
    if (!this.currentUser) return '??';
    const first = this.currentUser.firstName?.charAt(0) ?? '';
    const last = this.currentUser.lastName?.charAt(0) ?? '';
    return (first + last).toUpperCase() || '??';
  }
}
